import React, { useContext, createContext, useRef, useState, useEffect } from 'react';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';

/**
 * Interface for the video player context.
 * Defines the methods available for controlling the video player.
 */
export interface VideoPlayerContextType {
  // Public API for video control
  seekTo: (time: number) => void;
  getCurrentTime: () => number;
  play: () => void;
  pause: () => void;
  isPaused: () => boolean;

  // Video source methods
  getVideoUrl: () => string | undefined;

  // Internal method for ref handling - not intended for direct use
  setVideoRef: (element: HTMLVideoElement) => void;
}

// Create context for the video player
const VideoPlayerContext = createContext<VideoPlayerContextType | null>(null);

interface VideoPlayerProviderProps {
  children: React.ReactNode;
}

// Create a provider for the video player context
export const VideoPlayerProvider: React.FC<VideoPlayerProviderProps> = ({ children }) => {
  // Create a reference for the video element that will be controlled
  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);

  const videoCreator = useVideoCreatorStore();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const story = videoCreator.story;
    if (!story) return; // Wait for story to be loaded

    const originalVideo = story?.originalVideo;
    if (!originalVideo) return; // No video available

    // Prioritize the lowest quality URL for preview performance
    const newVideoUrl = originalVideo?.video?.mp4UrlLow ||
                       originalVideo?.video?.mp4Url ||
                       originalVideo?.video?.mp4UrlMedium ||
                       originalVideo?.video?.mp4UrlHigh;

    if (newVideoUrl) {
      console.log(`Setting video source: URL=${newVideoUrl}`);
      setVideoUrl(newVideoUrl);
    }
  }, [videoCreator.story]);

  // Define the video player API for the context
  const videoPlayerAPI: VideoPlayerContextType = {
    // Video control methods
    seekTo: (time: number) => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.currentTime = time;
      }
    },
    getCurrentTime: () => {
      return videoPlayerRef.current ? videoPlayerRef.current.currentTime : 0;
    },
    play: () => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.play().catch(err => console.error('Error playing video:', err));
      }
    },
    pause: () => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.pause();
      }
    },
    isPaused: () => {
      return videoPlayerRef.current ? videoPlayerRef.current.paused : true;
    },

    // Video source methods
    getVideoUrl: () => {
      return videoUrl || undefined;
    },

    // Internal method to set the video element reference
    setVideoRef: (element: HTMLVideoElement) => {
      videoPlayerRef.current = element;
    }
  };

  return (
    <VideoPlayerContext.Provider value={videoPlayerAPI}>
      {children}
    </VideoPlayerContext.Provider>
  );
};

/**
 * Custom hook that provides access to the VideoPlayer context.
 * This hook abstracts away the implementation details of the VideoPlayerContext
 * and provides a simple interface for controlling the video player.
 *
 * @returns VideoPlayerContextType or throws an error if used outside the VideoPlayerContext provider
 */
export function useVideoPlayer(): VideoPlayerContextType {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider');
  }
  return context;
}